<template>
  <section class="flex-1 flex flex-col bg-gray-100 border-l border-gray-200" style="height:calc(100vh - 64px);">
    <header class="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200">
      <div class="flex items-center gap-2 text-caption text-gray-600">
        <span class="inline-block w-2 h-2 rounded-full bg-green-500" aria-hidden="true" />
        <span>Live preview · {{ pathLabel }}</span>
      </div>
      <div class="flex items-center gap-0.5">
        <button
          v-for="opt in viewportOptions"
          :key="opt.name"
          type="button"
          :class="viewportButtonClass(opt.name)"
          :aria-pressed="viewport === opt.name"
          :aria-label="`${opt.label} preview`"
          :title="`${opt.label} preview`"
          @click="viewport = opt.name">
          <EditorIcon :name="opt.icon" class="w-4 h-4" />
        </button>
        <a
          :href="previewUrl"
          target="_blank"
          class="btn btn-link btn-sm ml-2"
          rel="noopener">
          Open
        </a>
      </div>
    </header>

    <div class="relative flex-1 overflow-auto flex justify-center bg-gray-200 p-4">
      <!-- Subtle reconcile shimmer while a structural edit reloads the iframe.
           Most edits live-patch instantly; this only shows on full reloads. -->
      <transition name="fade">
        <div
          v-if="reloading"
          class="absolute top-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 border border-gray-200 shadow-sm text-[11px] text-gray-600"
          role="status"
          aria-live="polite">
          <span class="w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" aria-hidden="true" />
          Updating preview…
        </div>
      </transition>
      <iframe
        ref="frame"
        :src="previewUrl"
        :style="frameStyle"
        class="bg-white shadow-sm border border-gray-300 transition-[width] duration-200"
        :title="`Live preview of ${pathLabel}`"
        @load="onFrameLoad" />
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import EditorIcon from './EditorIcon.vue'

const props = defineProps({
  websiteId: { type: String, required: true },
  slug: { type: String, default: '' },
  reloadKey: { type: Number, default: 0 }
})

const viewport = ref('desktop')
const frame = ref(null)
const reloading = ref(false)
let pendingScroll = 0

const viewportOptions = [
  { name: 'desktop', label: 'Desktop', icon: 'desktop' },
  { name: 'tablet', label: 'Tablet', icon: 'tablet' },
  { name: 'mobile', label: 'Mobile', icon: 'mobile' }
]

const filename = computed(() => {
  const s = (props.slug || 'index').replace(/^\//, '')
  return s === '' ? 'index.html' : `${s}.html`
})

const pathLabel = computed(() => filename.value)

// Iframe loads from /__edit/, which BuildSite writes in edit-mode: the
// HTML keeps data-editable* + data-item-uid attrs and carries the
// click-to-edit overlay <script>. The published-style HTML next door
// is what we deploy on Publish.
const previewUrl = computed(() => `/generated_websites/${props.websiteId}/__edit/${filename.value}`)

const frameStyle = computed(() => {
  const widths = { desktop: '100%', tablet: '820px', mobile: '390px' }
  return { width: widths[viewport.value] || '100%', height: '100%', maxWidth: '100%' }
})

function viewportButtonClass(name) {
  // DS icon buttons — the active viewport reads as "pressed" via the secondary
  // (outlined) treatment; the rest are ghost.
  return viewport.value === name ?
    'btn btn-secondary btn-icon btn-xs' :
    'btn btn-ghost btn-icon btn-xs'
}

// Same-page content reload (reloadKey bump): reload in place and restore the
// scroll position so an edit doesn't yank the preview back to the top — the
// previous implementation remounted the iframe via :key, flickering + losing
// scroll on every save.
watch(() => props.reloadKey, () => {
  const win = frame.value?.contentWindow
  if (!win) return
  reloading.value = true
  try { pendingScroll = win.scrollY || win.pageYOffset || 0 } catch (e) { pendingScroll = 0 }
  try { win.location.reload() } catch (e) {
    // Cross-origin or detached — fall back to re-setting src.
    if (frame.value) frame.value.src = previewUrl.value
  }
})

function onFrameLoad() {
  reloading.value = false
  if (!pendingScroll) return
  const win = frame.value?.contentWindow
  try { win?.scrollTo(0, pendingScroll) } catch (e) { /* ignore */ }
  pendingScroll = 0
}

// Live content patch — update an element in the preview instantly (no
// reload). Called by the parent on a field edit; the overlay applies it.
function patchField(payload) {
  const win = frame.value?.contentWindow
  if (!win) return
  try {
    win.postMessage({ source: 'mly-edit-parent', kind: 'patch-field', ...payload }, '*')
  } catch (e) { /* ignore */ }
}

// Ask the overlay to frame + scroll the element matching a field — keeps the
// preview visually linked to whatever field has focus in the editor.
function highlightField(payload) {
  const win = frame.value?.contentWindow
  if (!win) return
  try {
    win.postMessage({ source: 'mly-edit-parent', kind: 'highlight-field', ...payload }, '*')
  } catch (e) { /* ignore */ }
}

defineExpose({ patchField, highlightField })
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.15s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
